import type { ImageRef } from '@/lib/design/types'

import { mediaUrl } from '@/lib/media'

export const imageUrl = (ref: ImageRef | null | undefined): string | null => {
    if (!ref) return null
    if (ref.key) return mediaUrl(ref.key)
    if (ref.src) return ref.src

    return null
}

type Entry = {
    image: HTMLImageElement | null
    promise: Promise<HTMLImageElement | null>
}

const cache = new Map<string, Entry>()
const listeners = new Set<() => void>()

// Same-origin image loading, so slides drawn on a canvas can always be exported.
export const loadImage = (ref: ImageRef | null | undefined): Promise<HTMLImageElement | null> => {
    const url = imageUrl(ref)

    if (!url || typeof window === 'undefined') return Promise.resolve(null)

    const existing = cache.get(url)

    if (existing) return existing.promise

    const entry: Entry = { image: null, promise: Promise.resolve(null) }

    entry.promise = new Promise(resolve => {
        const image = new Image()

        image.decoding = 'async'
        image.onload = () => {
            entry.image = image
            listeners.forEach(listener => listener())
            resolve(image)
        }
        image.onerror = () => resolve(null)
        image.src = url
    })

    cache.set(url, entry)

    return entry.promise
}

export const getLoadedImage = (ref: ImageRef | null | undefined): HTMLImageElement | null => {
    const url = imageUrl(ref)

    if (!url) return null

    const entry = cache.get(url)

    if (!entry) {
        void loadImage(ref)

        return null
    }

    return entry.image
}

export const onImageLoaded = (listener: () => void): (() => void) => {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}
